import React from 'react';
import * as Icons from '@ant-design/icons';

/** 默认可选图标名（与 sinolinkway_app 等使用 iconRenderer 的项目一致，均为 @ant-design/icons 导出名） */
const DEFAULT_ICON_NAMES: string[] = [
  'AppstoreOutlined',
  'ShopOutlined',
  'ShoppingOutlined',
  'ShoppingCartOutlined',
  'FileTextOutlined',
  'PictureOutlined',
  'ImageOutlined',
  'HomeOutlined',
  'UserOutlined',
  'TeamOutlined',
  'SettingOutlined',
  'SafetyOutlined',
  'DatabaseOutlined',
  'BarChartOutlined',
  'QuestionCircleOutlined',
  'TagsOutlined',
  'TagOutlined',
  'FolderOutlined',
  'FolderOpenOutlined',
  'BookOutlined',
  'ReadOutlined',
  'CustomerServiceOutlined',
  'MailOutlined',
  'PhoneOutlined',
  'EnvironmentOutlined',
  'GlobalOutlined',
  'ThunderboltOutlined',
  'FireOutlined',
  'StarOutlined',
  'HeartOutlined',
  'GiftOutlined',
  'CrownOutlined',
  'TrophyOutlined',
  'RocketOutlined',
  'BulbOutlined',
  'ExperimentOutlined',
  'ToolOutlined',
  'ApiOutlined',
  'CodeOutlined',
  'CloudOutlined',
  'DesktopOutlined',
  'MobileOutlined',
  'TabletOutlined',
  'LaptopOutlined',
  'CameraOutlined',
  'VideoCameraOutlined',
  'AudioOutlined',
  'NotificationOutlined',
  'BellOutlined',
  'MessageOutlined',
  'CommentOutlined',
  'FormOutlined',
  'UnorderedListOutlined',
  'MenuOutlined',
  'LayoutOutlined',
  'DashboardOutlined',
  'PartitionOutlined',
  'CarOutlined',
  'SafetyCertificateOutlined',
  'CheckCircleOutlined',
  'ClockCircleOutlined',
  'SendOutlined',
  'InsuranceOutlined',
];

/** 获取默认图标名列表（用于下拉选择，与 iconRenderer 可渲染的图标一致） */
export const getDefaultIconNames = (): string[] => DEFAULT_ICON_NAMES;

/** 根据菜单路径/类型返回推荐图标名（用于菜单与面包屑无 icon 或 icon 无效时的回退） */
const PATH_ICON_MAP: Record<string, string> = {
  '/': 'DashboardOutlined',
  '/system': 'SettingOutlined',
  '/system/manager': 'UserOutlined',
  '/system/managerRole': 'TeamOutlined',
  '/system/managerPermission': 'SafetyOutlined',
  '/system/systemLog': 'FileTextOutlined',
  '/user': 'TeamOutlined',
  '/user/userList': 'UserOutlined',
  '/user/userRole': 'TeamOutlined',
  '/user/userPermission': 'SafetyOutlined',
  '/data': 'DatabaseOutlined',
  '/data/dataMark': 'DatabaseOutlined',
  '/data/dashboard': 'BarChartOutlined',
  '/cms': 'AppstoreOutlined',
  '/cms/category': 'AppstoreOutlined',
  '/cms/product': 'ShoppingOutlined',
  '/cms/news': 'FileTextOutlined',
  '/cms/banner': 'PictureOutlined',
  '/cms/siteConfig': 'SettingOutlined',
  '/cms/faq': 'QuestionCircleOutlined',
  '/cms/contactSubmission': 'MailOutlined',
  '/cms/chat': 'CommentOutlined',
  '/login': 'SafetyOutlined',
};

export const getIconNameByPath = (path?: string): string | undefined => {
  if (!path) return undefined;
  const normalized = path.replace(/\/edit\/[^/]+$/, '').replace(/\/create$/, '');
  return PATH_ICON_MAP[normalized] ?? PATH_ICON_MAP[normalized.split('/').slice(0, 2).join('/')] ?? PATH_ICON_MAP['/'];
};

// 动态渲染图标函数；path 有映射时优先按路径显示图标，保证各菜单项图标区分；后端传自定义图片 URL 时仍用图片
export const renderIcon = (iconName?: string, path?: string): React.ReactNode => {
  const pathIconName = path ? getIconNameByPath(path) : undefined;
  const nameToUse = (iconName?.startsWith('https://') ? iconName : undefined) || pathIconName || iconName;
  if (!nameToUse) {
    return <Icons.AppstoreOutlined />; // 默认图标
  }

  // 检查是否为 HTTPS 地址（自定义图片 URL）
  if (nameToUse.startsWith('https://')) {
    return (
      <img
        src={nameToUse}
        alt='icon'
        style={{
          width: 16,
          height: 16,
          objectFit: 'contain',
        }}
        onError={e => {
          // 图片加载失败时显示默认图标
          const target = e.target as HTMLImageElement;
          target.style.display = 'none';
          // 创建一个 span 元素来显示默认图标
          const iconSpan = document.createElement('span');
          iconSpan.innerHTML = '📱'; // 使用简单的 emoji 作为备用
          target.parentElement?.appendChild(iconSpan);
        }}
      />
    );
  }

  const iconKey = nameToUse;
  const IconComponent = (Icons as unknown as Record<string, React.ComponentType>)[iconKey];
  if (IconComponent && typeof IconComponent === 'function') {
    return React.createElement(IconComponent);
  }

  // 若仍无效则用路径回退或默认
  const finalFallback = pathIconName && pathIconName !== iconKey
    ? (Icons as unknown as Record<string, React.ComponentType>)[pathIconName]
    : null;
  if (finalFallback && typeof finalFallback === 'function') {
    return React.createElement(finalFallback);
  }
  return <Icons.AppstoreOutlined />;
};
